<template>
  <div id="announcement">
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-warning header-enhanced">
      <!-- Announcement Statistics Cards -->
      <b-row class="mb-4">
        <b-col xl="3" md="6">
          <stats-card
            title="Total Announcements"
            type="gradient-warning"
            :sub-title="stats.totalAnnouncements ? stats.totalAnnouncements.toLocaleString() : 'Loading...'"
            icon="ni ni-bell-55"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-warning mr-2">↗ {{ stats.announcementGrowth }}%</span>
              <span class="text-nowrap">vs last month</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Active"
            type="gradient-success"
            :sub-title="stats.activeAnnouncements ? stats.activeAnnouncements.toLocaleString() : 'Loading...'"
            icon="ni ni-check-bold"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-success mr-2">● Live</span>
              <span class="text-nowrap">Currently active</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Scheduled"
            type="gradient-info"
            :sub-title="stats.scheduledAnnouncements ? stats.scheduledAnnouncements.toLocaleString() : 'Loading...'"
            icon="ni ni-time-alarm"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-info mr-2">⏰ Pending</span>
              <span class="text-nowrap">Scheduled to send</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Total Recipients"
            type="gradient-purple"
            :sub-title="stats.totalRecipients ? stats.totalRecipients.toLocaleString() : 'Loading...'"
            icon="ni ni-single-02"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-purple mr-2">👥 Users</span>
              <span class="text-nowrap">Will receive</span>
            </template>
          </stats-card>
        </b-col>
      </b-row>

      <!-- Page Title -->
      <b-row align-v="center" class="mb-3">
        <b-col>
          <h3 class="text-white mb-0">
            <i class="ni ni-bell-55 mr-2"></i>Announcement Management
          </h3>
          <p class="text-white-50 mb-0">Create and manage system announcements</p>
        </b-col>
        <b-col class="text-right">
          <b-button
            variant="light"
            size="lg"
            class="shadow-sm add-announcement-btn"
            @click="addAnnouncement">
            <i class="ni ni-fat-add mr-2"></i>Create Announcement
          </b-button>
        </b-col>
      </b-row>
    </base-header>

    <div class="container-fluid mt--7">
      <!-- Enhanced Action Bar -->
      <b-card class="mb-4 shadow-sm action-bar-card">
        <b-row align-v="center">
          <b-col>
            <h5 class="mb-0 text-dark">
              <i class="ni ni-bullet-list-67 mr-2 text-primary"></i>
              All Announcements
            </h5>
            <small class="text-muted">Manage system announcements and notifications</small>
          </b-col>
        </b-row>
      </b-card>

      <!-- Announcement List -->
      <vuetable data-path="announcements"
                detail-row-component="announcement-detail"
                pagination-path="pagination"
                ref="vuetable"
                track-by="announcementId"
                :api-url="apiUrl"
                :fields="fields"
                :http-options="httpOptions"
                :css="css.table"
                :per-page="10"
                :append-params="moreParams"
                :query-params="{
                  sort    : 'sort',
                  page    : 'page',
                  perPage : 'perPage'
                }"
                @vuetable:pagination-data="onPaginationData"
                @vuetable:loading="showLoader"
                @vuetable:loaded="hideLoader">
        <template slot="more" slot-scope="props">
          <transition name="slide">
            <div class="expand-icon" @click="showRow(props.rowData.announcementId)"
              v-if="visibleRowId !== props.rowData.announcementId">
              <i class="ni ni-bold-down expand-icon-arrow"></i>
              <span class="expand-text">Expand</span>
            </div>

            <div class="expand-icon" @click="hideRow(props.rowData.announcementId)" v-else>
              <i class="ni ni-bold-up expand-icon-arrow"></i>
              <span class="expand-text">Collapse</span>
            </div>
          </transition>
        </template>
      </vuetable>

      <!-- Enhanced Pagination -->
      <b-card class="mt-4 shadow-sm pagination-card">
        <b-row align-v="center">
          <b-col md="6">
            <div class="pagination-info">
              <vuetable-pagination-info ref="paginationInfo" class="mb-0 text-muted"></vuetable-pagination-info>
            </div>
          </b-col>
          <b-col md="6" class="text-right">
            <div class="pagination-controls">
              <vuetable-pagination
                ref="pagination"
                @vuetable-pagination:change-page="onChangePage"
                :css="css.pagination">
              </vuetable-pagination>
            </div>
          </b-col>
        </b-row>
      </b-card>
    </div>

    <!-- Modal -->
    <modals-container />
    <v-dialog/>

    <!-- Loading -->
    <loading :active.sync="isLoading"
              :is-full-page="true"
              background-color="#000"
              color="#068bcd"
              :opacity="0.5"
              :width="70"
              :height="70"
    ></loading>
  </div>
</template>

<script>
  const AddAnnouncement = () => import('./AddAnnouncement.vue');
  const AnnouncementListActions = () => import('./AnnouncementListActions.vue');
  const AnnouncementDetail = () => import('./AnnouncementDetail.vue');
  const Loading = () => import('vue-loading-overlay');
  const Vuetable = () => import('vuetable-2/src/components/Vuetable');
  const VuetablePagination =
    () => import('vuetable-2/src/components/VuetablePagination');
  const VuetablePaginationInfo =
    () => import('vuetable-2/src/components/VuetablePaginationInfo');
  
  import 'vue-loading-overlay/dist/vue-loading.css';
  import moment from 'moment-timezone';
  import TableStyle from '../table-bootstrap-css.js';
  import Vue from 'vue';
  import _ from 'lodash';
  import baseHeader from '../../BaseHeader';
  import StatsCard from '../../Cards/StatsCard.vue';


  Vue.component('announcement-detail', AnnouncementDetail);
  Vue.component('announcement-list-actions', AnnouncementListActions);

  export default {
    name : 'AnnouncementList',
    data() {
      return {
        isLoading   : false,
        adminTz     : this.$store.getters['user/user'].timezone,
        apiUrl      : process.env.VUE_APP_ROOT_API + 'api/announcement/all',
        moreParams  : {},
        css         : TableStyle,
        httpOptions : {
          headers : {
            'Authorization' : `Bearer ${this.$store.state.auth.accessToken}`,
          },
        },
        stats: {
          totalAnnouncements: null,
          activeAnnouncements: null,
          scheduledAnnouncements: null,
          totalRecipients: null,
          announcementGrowth: 0,
        },
        fields : [ {
          name       : '__slot:more',
          title      : '',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : 'announcementId',
          title      : 'ID',
          sortField  : 'announcement_id',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : 'title',
          title      : 'Title',
          sortField  : 'title',
          direction  : 'desc',
          titleClass : 'text-center',
        }, {
          name       : 'platformDisp',
          title      : 'Platform',
          sortField  : 'platformDisp',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : 'filterDisp',
          title      : 'Filter',
          sortField  : 'filterDisp',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        },{
          name       : 'link',
          title      : 'Link',
          sortField  : 'link',
          direction  : 'desc',
          titleClass : 'text-center',
        },
        // {
        //   name       : 'startSchedule',
        //   title      : 'Start Date',
        //   sortField  : 'start_date',
        //   direction  : 'desc',
        //   titleClass : 'text-center',
        // }, 
        // {
        //   name       : 'endSchedule',
        //   title      : 'End Date',
        //   sortField  : 'end_date',
        //   direction  : 'desc',
        //   titleClass : 'text-center',
        // }, 
        // {
        //   name       : 'status',
        //   title      : 'Status',
        //   sortField  : 'status',
        //   direction  : 'desc',
        //   titleClass : 'text-center',
        //   dataClass  : 'text-center',
        // }, 
        {
          name       : '__component:announcement-list-actions',
          title      : 'Actions',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        } ],
        visibleRowId : null,
      }
    },
    components : {
      Loading,
      Vuetable,
      VuetablePagination,
      VuetablePaginationInfo,
      baseHeader,
      StatsCard
    },
    watch : {
      visibleRowId(newValue, oldValue) {
        if (newValue && oldValue)
          this.$refs.vuetable.hideDetailRow(oldValue);
      },
    },
    methods : {

      /**
       * Open Add Announcement Modal
       */
      addAnnouncement() {
        this.$modal.show(AddAnnouncement, {}, {
          height       : 'auto',
          adaptive     : true,
          clickToClose : false,
        }, {
          'reload-table' : (event) => {
            this.$refs.vuetable.reload();
          },
        });
      },

      /**
       * For Pagination
       * @param data
       * @return {object}
       */
      transform(data) {

        console.log('data mmg', data.data);

        const transformed = {};

        transformed.pagination = {
          'total'         : data.total,
          'per_page'      : data.perPage,
          'current_page'  : data.currentPage,
          'last_page'     : data.lastPage,
          'next_page_url' : data.nextPageUrl,
          'pre_page_url'  : data.prevPageUrl,
          'from'          : data.from,
          'to'            : data.to,
        };

        transformed.announcements = [];

        for (let i = 0; i < data.data.length; i++) {
          const selectedTz = data.data[i].schedule_timezone;

          // Created Date conversion from UTC to Local TZ
          const createdConv = moment.utc(data.data[i].created).local()
            .format('MMM DD, YYYY hh:mm A z');
          
          // Start Date conversion from UTC to Admin TZ & Selected TZ
          const startUTC = moment.utc(data.data[i].start_date);
          const startConv = moment.utc(data.data[i].start_date).local()
            .format('MMM DD, YYYY hh:mm A z');
          const startSelectedTz = moment.tz(startUTC, selectedTz);

          // End Date conversion from UTC to Local TZ & Selected TZ
          const endUTC = moment.utc(data.data[i].end_date);
          const endConv = moment.utc(data.data[i].end_date).local()
            .format('MMM DD, YYYY hh:mm A z');
          const endSelectedTz = moment.tz(endUTC, selectedTz);

          // End Date conversion from UTC to Local TZ & Selected TZ
          const dispatchUTC = moment.utc(data.data[i].dispatch_date);
          const dispatchConv = moment.utc(data.data[i].dispatch_date).local()
            .format('MMM DD, YYYY hh:mm A z');
          const dispatchSelectedTz = moment.tz(dispatchUTC, selectedTz);

          transformed.announcements.push({
            announcementId : data.data[i].announcement_id,
            title          : data.data[i].title,
            filterDisp     : _.capitalize(data.data[i].filter),
            platformDisp   : _.capitalize(data.data[i].platform),
            startSchedule  : (data.data[i].start_date ===
              '0000-00-00 00:00:00') ? '' : startConv,
            endSchedule : (data.data[i].end_date === '0000-00-00 00:00:00') ?
              '' : endConv,
            dispatcSchedule: (data.data[i].dispatch_date === '0000-00-00 00:00:00') ?
              '' : dispatchConv,
            status      : _.capitalize(data.data[i].status),
            createdDate : createdConv,
            recipient   : data.data[i].recipient,

            // For Display
            startDateDisp : (data.data[i].start_date ===
              '0000-00-00 00:00:00') ? null :
              startSelectedTz.format('MMM DD, YYYY hh:mm A z'),
            endDateDisp : (data.data[i].end_date ===
              '0000-00-00 00:00:00') ? null :
              endSelectedTz.format('MMM DD, YYYY hh:mm A z'),
            dispatchDateDisp: (data.data[i].dispatch_date ===
              '0000-00-00 00:00:00') ? null :
              dispatchSelectedTz.format('MMM DD, YYYY hh:mm A z'),

            // Additional Info For Edit Announcement
            content   : data.data[i].content,
            filter    : data.data[i].filter,
            platform  : data.data[i].platform,
            timezone  : (selectedTz === "") ? null : selectedTz,
            startDate : (data.data[i].start_date ===
              '0000-00-00 00:00:00') ? null :
              startSelectedTz.format('YYYY-MM-DD'),
            startTime : (data.data[i].start_date ===
              '0000-00-00 00:00:00') ? null :
              startSelectedTz.format('HH:mm'),
            endDate : (data.data[i].end_date ===
              '0000-00-00 00:00:00') ? null :
              endSelectedTz.format('YYYY-MM-DD'),
            endTime : (data.data[i].end_date ===
              '0000-00-00 00:00:00') ? null :
              endSelectedTz.format('HH:mm'),
            dispatchDate: (data.data[i].dispatch_date ===
              '0000-00-00 00:00:00') ? null :
              dispatchSelectedTz.format('YYYY-MM-DD'),
            dispatchTime: (data.data[i].dispatch_date ===
              '0000-00-00 00:00:00') ? null :
              dispatchSelectedTz.format('HH:mm'),
            link : data.data[i].link,
            mediaType : data.data[i].media_type,
            playlist: data.data[i].playlist_id,
          });
        }

        return transformed;
      },

      /**
       * Update Paginaton info
       * @param paginationData
       */
      onPaginationData(paginationData) {
        this.$refs.pagination.setPaginationData(paginationData);
        this.$refs.paginationInfo.setPaginationData(paginationData);
      },

      /**
       * Update Vuetable page
       * @param page
       */
      onChangePage(page) {
        this.$refs.vuetable.changePage(page);
      },

      /**
       * Toggle More Detail
       */
      showRow(id) {
        this.$refs.vuetable.showDetailRow(id);
        this.visibleRowId = id;
      },

      hideRow(id) {
        this.$refs.vuetable.hideDetailRow(id);
        this.visibleRowId = null;
      },

      /**
       * Show Loader
       */
      showLoader() {
        this.isLoading = true;
      },

      /**
       * Hide Loader
       */
      hideLoader() {
        this.isLoading = false;
      },

      /**
       * Initialize Announcement Statistics
       */
      initializeStats() {
        // Simulated announcement statistics - replace with actual API calls
        this.stats.totalAnnouncements = 47;
        this.stats.activeAnnouncements = 23;
        this.stats.scheduledAnnouncements = 12;
        this.stats.totalRecipients = 15420;
        this.stats.announcementGrowth = 15.3;
      },

    },
    mounted() {
      this.initializeStats();
      this.$store.dispatch('address/getCountries');
      this.$events.$on('loading-state',
                       eventData => {
                         if (eventData)
                           this.showLoader();
                         else
                           this.hideLoader();
                       });
      this.$events.$on('reload', () => {
        this.$refs.vuetable.refresh();
      });
    },
  }
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/components/admin/announcement/announcement-list";

/* Enhanced Header Styles */
.baseHeader {
  margin-top: -80px;
  position: relative;
  overflow: hidden;
}

.baseHeader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.9) 0%, rgba(255, 152, 0, 0.9) 100%);
  z-index: 1;
}

.baseHeader::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(255, 193, 7, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(255, 152, 0, 0.2) 0%, transparent 50%);
  z-index: 1;
}

.header-enhanced .card {
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.header-enhanced .text-white {
  color: #fff !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-enhanced .card-body {
  position: relative;
  z-index: 2;
}

/* Stats Cards Hover Effects */
.stats-card-hover {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
}

.stats-card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.stats-card-hover .card-body {
  transition: all 0.3s ease;
}

.stats-card-hover:hover .card-body {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
}

/* Enhanced Action Bar */
.action-bar-card {
  border-radius: 12px !important;
  border: none !important;
  transition: all 0.3s ease;
}

.action-bar-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
}

.add-announcement-btn {
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 193, 7, 0.3);
}

.add-announcement-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 193, 7, 0.4);
}

/* Enhanced Table Styles */
table {
  z-index: 99;
  position: relative;
  background: white;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
  animation: fadeInUp 0.8s ease-out;
}

.vuetable th {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
  border-bottom: 2px solid #dee2e6 !important;
  font-weight: 600 !important;
  color: #495057 !important;
  padding: 12px 8px !important;
}

.vuetable td {
  padding: 12px 8px !important;
  border-bottom: 1px solid #f1f3f4 !important;
  vertical-align: middle !important;
}

.vuetable tbody tr:hover {
  background-color: rgba(255, 193, 7, 0.05) !important;
  transform: scale(1.01);
  transition: all 0.2s ease;
}

/* Enhanced Expand Icons */
.expand-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(255, 193, 7, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 193, 7, 0.2);
}

.expand-icon:hover {
  background: rgba(255, 193, 7, 0.2);
  transform: scale(1.05);
}

.expand-icon-arrow {
  font-size: 14px;
  color: #ffc107;
  margin-right: 4px;
}

.expand-text {
  font-size: 12px;
  font-weight: 600;
  color: #856404;
}

/* Enhanced Pagination */
.pagination-card {
  border-radius: 12px !important;
  border: none !important;
  background: rgba(248, 249, 250, 0.8);
}

.pagination-info {
  padding: 16px 20px;
  font-weight: 500;
  color: #6c757d;
}

.pagination-controls {
  padding: 16px 20px;
}

/* Slide Animation for Expand/Collapse */
.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter, .slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.action-bar-card {
  animation: fadeInUp 0.8s ease-out;
}

/* Responsive Design */
@media (max-width: 768px) {
  .stats-card-hover:hover {
    transform: none;
  }

  .action-bar-card:hover {
    transform: none;
  }

  .add-announcement-btn:hover {
    transform: none;
  }

  .expand-icon:hover {
    transform: none;
  }

  .vuetable tbody tr:hover {
    transform: none;
  }

  .expand-icon {
    padding: 6px 8px;
  }

  .expand-text {
    display: none;
  }
}

/* Legacy styles */
.baseHeader, .header {
  margin-top: -80px;
}
</style>